import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ActionList, StatusMessage } from '../../../ui';
import SortIcon from '../../../ui/Sorting';
import SortSummary from '../../../ui/SortSummary';
import { formatDateTime } from '../../../../helpers/formatDateTime';
import { getAlarmSections, alarmSectionsToText, alarmSectionsToHtml } from '../../../../helpers/formatAlarmClipboard';
import type { BureauAlarm } from '../../../../services/bureauManagement/bureauManagement.types';
import { localAcknowledge } from '../../../../services/bureauManagement/bureauManagement.service';
import { getProfile, lookupUsers } from '../../../../services/userManagement/userManagement.service';
import type { UserLookupItem } from '../../../../services/userManagement/userManagement.types';
import { getRecipient, listRecipients } from '../../../../services/distribution/distribution.service';
import { useAuthContext } from '../../../../contexts/useAuthContext';
import { alarmAcknowledgedEvent } from '../../../../services/events/alarmEvents';
import Modal from '../../../ui/Modal';
import { ModalContent } from '../../../ui/ModalContent';
import { TOAST_SUCCESS_MS, TOAST_ERROR_MS, TOAST_LONG_MS } from '../../../../constants/timeouts';
import { getInitialsFromName } from '../../../../layout/sidebar/helpers/sidebarHelpers';
import LoadingSpinner from '../../../ui/LoadingSpinner';

function getUserDisplayName(user: UserLookupItem): string {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return user.displayName?.trim() || fullName || user.employeeId?.trim() || 'Unknown User';
}

function colorFromName(s?: string | null): string {
  const palette = ['#6C5CE7', '#00B894', '#0984E3', '#FD79A8', '#F6C85F', '#00B8D9', '#FF7A00'];
  if (!s) { return palette[0]; }
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); }
  return palette[Math.abs(h) % palette.length];
}

// Safely read one of several possible keys from variant backend shapes
const getVariant = <T,>(obj: unknown, keys: string[]): T | undefined => {
  if (!obj || typeof obj !== 'object') {return undefined;}
  const rec = obj as Record<string, unknown>;
  for (const k of keys) {
    const v = rec[k];
    if (v !== undefined && v !== null) {return v as T;}
  }
  return undefined;
};

const SORT_COLUMN_LABELS: Record<string, string> = {
  siteName: 'Site Name',
  path: 'Path',
  priority: 'Priority',
  last: 'Last Occurrence',
  sourceAlarmState: 'Alarm State',
  acknowledgedBy: 'Acknowledged By',
};

interface Props {
  filteredData: BureauAlarm[];
  page?: number;
  pageSize?: number;
  totalCount?: number;
  selectedIds?: Record<number, boolean>;
  onToggleSelect?: (id: number) => void;
  onToggleSelectAll?: () => void;
  onSelect?: (item: BureauAlarm) => void;
  onAcknowledged?: (count?: number, acknowledgedAlarmIds?: number[]) => void;
  disableSiteLink?: boolean;
  sortColumn?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  onResetSort?: () => void;
  showSortSummary?: boolean;
  isLoading?: boolean;
  headerActions?: React.ReactNode;
  headerSubtitle?: React.ReactNode;
}

export const BureauCategoryTable: React.FC<Props> = ({ 
  filteredData, 
  page = 1,
  pageSize,
  totalCount,
  selectedIds = {},
  onToggleSelect,
  onToggleSelectAll,
  onAcknowledged,
  disableSiteLink = false,
  sortColumn: sortColumnProp,
  sortOrder: sortOrderProp,
  onSort,
  onResetSort,
  showSortSummary = true,
  isLoading = false,
  headerActions,
  headerSubtitle,
}) => {
  const isControlled = typeof onSort === 'function';
  const { userProfile } = useAuthContext();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [acknowledgingId, setAcknowledgingId] = useState<number | null>(null);
  const [sortColumnInternal, setSortColumnInternal] = useState<string>('');
  const [sortOrderInternal, setSortOrderInternal] = useState<'asc' | 'desc'>('asc');
  const [hasAppliedSort, setHasAppliedSort] = useState(false);
  const sortColumn = isControlled ? (sortColumnProp ?? '') : sortColumnInternal;
  const sortOrder = isControlled ? (sortOrderProp ?? 'asc') : sortOrderInternal;
  const [userMap, setUserMap] = useState<Record<string, string>>({});
  // Cache acknowledged alarms locally since backend doesn't return localAckBy
  const [acknowledgedCache, setAcknowledgedCache] = useState<Record<number, { userId: string; userName: string; timestamp: string }>>({});
  // Map recipientId -> contactValue (email/SMS) from distribution service
  const [recipientContactMap, setRecipientContactMap] = useState<Record<number, string>>({});
  // Tooltip state for message and acknowledged by columns
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [tooltipText, setTooltipText] = useState<string>('');
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left?: number }>({ top: 0, left: 0 });

  // Fetch distribution recipients on mount to build recipientId -> contactValue map
  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        const response = await listRecipients();
        if (response?.success && response.data) {
          const map: Record<number, string> = {};
          response.data.forEach(r => {
            if (r.recipientId && r.contactValue) {
              map[r.recipientId] = r.contactValue;
            }
          });
          setRecipientContactMap(map);
        }
      } catch (error) {
        console.info('Failed to fetch recipients for contact mapping:', error);
      }
    };
    fetchRecipients();
  }, []);

  // Fetch users on mount to create GUID -> name mapping
  useEffect(() => {
    let cancelled = false;
    const fetchUsers = async () => {
      try {
        const response = await lookupUsers();
        if (cancelled) return;
        if (response?.success && response.data) {
          const map: Record<string, string> = {};
          response.data.forEach(user => {
            const displayName = getUserDisplayName(user);
            // Map by GUID (primary)
            if (user.id) { map[user.id] = displayName; }
            // Map by employeeId (fallback for legacy acknowledged-by values)
            if (user.employeeId) { map[user.employeeId] = displayName; }
          });
          setUserMap(map);
        } else {
          console.info('Failed to fetch users for acknowledgment mapping:', response?.error);
        }
      } catch (error) {
        if (!cancelled) console.info('Failed to fetch users for acknowledgment mapping:', error);
      }
    };
    void fetchUsers();
    return () => { cancelled = true; };
  }, []);

  // Helper: resolve a raw value (GUID, employeeId, or name) to a full name via userMap
  const resolveUserName = (raw: string | null | undefined): string | null => {
    if (!raw) { return null; }
    // If it's a known key in the map (GUID or employeeId), return the mapped name
    if (userMap[raw]) { return userMap[raw]; }
    // Otherwise return the raw value as-is (it may already be a display name)
    return raw;
  };

  // Helper function to get acknowledged by display name
  const getAcknowledgedByName = (alarm: BureauAlarm): string | null => {
    const alarmId = alarm.bureauAlarmId ?? alarm.id ?? null;

    // First check our local cache (for newly acknowledged alarms in this session)
    if (alarmId && acknowledgedCache[alarmId]) {
      return resolveUserName(acknowledgedCache[alarmId].userName);
    }

    // Backend may return the GUID or employee ID - try both case variations first
    const userKeyVal = getVariant<string>(alarm, ['localAckBy', 'LocalAckBy', 'acknowledgedBy']);
    if (userKeyVal && userMap[userKeyVal]) {return userMap[userKeyVal];}

    // localAckByName / acknowledgedByName may contain a federated ID — resolve through map
    const rawNameVal = getVariant<string>(alarm, ['localAckByName', 'LocalAckByName', 'acknowledgedByName']);
    if (rawNameVal) {return resolveUserName(rawNameVal);}

    return null;
  };

  // Helper function to get the acknowledged-at timestamp for display under the avatar
  const getAcknowledgedAtDate = (alarm: BureauAlarm): string | null => {
    const alarmId = alarm.bureauAlarmId ?? alarm.id ?? null;

    if (alarmId && acknowledgedCache[alarmId]) {
      return formatDateTime(acknowledgedCache[alarmId].timestamp);
    }

    const ackAt = [getVariant<string | number>(alarm, ['ackDate', 'AckDate', 'ack_date', 'ackDateTime', 'AckDateTime', 'ack_date_time']),
      alarm.localAckAt,
      getVariant<string | number>(alarm, ['LocalAckAt', 'acknowledgedAt', 'local_ack_at', 'acknowledged_at']),
      ...Object.entries(alarm as Record<string, unknown>)
        .filter(([key]) => ['ackdate', 'ack_date', 'ackdatetime', 'ack_date_time', 'localackat', 'local_ack_at', 'acknowledgedat', 'acknowledged_at'].includes(key.toLowerCase()))
        .map(([, value]) => value as string | number)]
      .find(value => value !== undefined && value !== null && value !== '');
    if (ackAt === undefined) {return null;}

    const formatted = formatDateTime(String(ackAt));
    return formatted || null;
  };

  const computeTooltipStyle = (
    position: { top: number; left?: number }
  ): React.CSSProperties => {
    const style: React.CSSProperties = {
      position: 'fixed',
      top: `${position.top}px`,
      zIndex: 9999,
      pointerEvents: 'none',
    };
    if (position.left !== undefined) {
      style.left = `${position.left}px`;
    }
    return style;
  };

  const handleMouseEnterTooltip = (e: React.MouseEvent<HTMLElement>, text: string, ackDate?: string | null) => {
    if (!text || text === '—') {
      setShowTooltip(false);
      return;
    }

    const tooltipTextValue = ackDate ? `${text} - ${ackDate}` : text;
    const rect = e.currentTarget.getBoundingClientRect();
    
    setTooltipPosition({
      top: rect.bottom + 8,
      left: rect.left
    });
    setTooltipText(tooltipTextValue);
    setShowTooltip(true);
  };

  const handleMouseLeaveTooltip = () => {
    setShowTooltip(false);
  };

  const handleSort = (column: string) => {
    setHasAppliedSort(true);
    if (isControlled) {
      onSort!(column);
      return;
    }
    if (sortColumnInternal === column) {
      setSortOrderInternal(sortOrderInternal === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumnInternal(column);
      setSortOrderInternal('asc');
    }
  };

  const handleResetSort = () => {
    if (isControlled) {
      onResetSort?.();
    } else {
      setSortColumnInternal('');
      setSortOrderInternal('asc');
    }
    setHasAppliedSort(false);
  };

  const showSummary = showSortSummary && hasAppliedSort && Boolean(sortColumn)
    && (!isControlled || Boolean(onResetSort));

  const sortedData = useMemo(() => {
    // When controlled, parent has already sorted; skip internal sort
    if (isControlled) {return filteredData;}
    if (!filteredData || filteredData.length === 0) {return filteredData;}
    const copy = [...filteredData];
    const getString = (item: BureauAlarm, key: string) => {
      switch (key) {
        case 'siteName':
          return String(item.siteName || item.siteIdentifier || '').toLowerCase();
        case 'siteIdentifier':
          return String(item.siteIdentifier || item.siteName || '').toLowerCase();
        case 'recipient':
          return String(item.bureauRecipientName || item.bureauName || item.bureauEndpoint || '').toLowerCase();
        case 'path':
          return String(item.pathToAlarm || item.path || item.alarmIdentifier || '').toLowerCase();
        case 'sourceAlarmState':
          return String(item.sourceAlarmState || '').toLowerCase();
        case 'alarmMessage':
          return String(item.alarmMessage || '').toLowerCase();
        case 'acknowledgedBy':
          return String(getAcknowledgedByName(item) || '').toLowerCase();
        default:
          return '';
      }
    };

    const getDate = (item: BureauAlarm, key: string) => {
      switch (key) {
        case 'last':
          return item.lastOccurrenceAt ? new Date(item.lastOccurrenceAt).getTime() : 0;
        default:
          return 0;
      }
    };

    const getPriority = (item: BureauAlarm) => item.priority ?? -1; // -1 for null/undefined to sort them last

    const getAck = (item: BureauAlarm) => (item.localAck ? 1 : 0);

    if (!sortColumn) {return copy;}

    copy.sort((a, b) => {
      let res = 0;
      if (['siteName', 'siteIdentifier', 'recipient', 'path', 'sourceAlarmState', 'alarmMessage', 'acknowledgedBy'].includes(sortColumn)) {
        const av = getString(a, sortColumn);
        const bv = getString(b, sortColumn);
        if (av < bv) {res = -1;}
        else if (av > bv) {res = 1;}
        else {res = 0;}
      } else if (sortColumn === 'last') {
        const av = getDate(a, sortColumn);
        const bv = getDate(b, sortColumn);
        res = av - bv;
      } else if (sortColumn === 'priority') {
        res = getPriority(a) - getPriority(b);
      } else if (sortColumn === 'ack') {
        res = getAck(b) - getAck(a);
      }
      else {
        console.info('Unknown sort column:', sortColumn);
      }

      return sortOrder === 'asc' ? res : -res;
    });

    return copy;
  }, [filteredData, sortColumn, sortOrder, userMap]);

  //modal variables
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
  const [modalHeadingText, setModalHeadingText] = useState<string | undefined>(undefined);
  const [modalMessageText, setModalMessageText] = useState<React.ReactNode | undefined>(undefined);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
  // view modal state
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  const [viewAlarm, setViewAlarm] = useState<BureauAlarm | null>(null);

  // Lock the wrapper's min-height when loading starts so new (fewer) rows don't
  // collapse the page height and cause a scroll jump. Released via CSS transition.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) { return undefined; }
    if (isLoading) {
      el.style.minHeight = `${el.offsetHeight}px`;
      return undefined;
    }
    const id = requestAnimationFrame(() => {
      if (wrapperRef.current) { wrapperRef.current.style.minHeight = ''; }
    });
    return () => cancelAnimationFrame(id);
  }, [isLoading]);

  // Scroll tbody to top on page change
  useEffect(() => {
    if (tbodyRef.current) {
      tbodyRef.current.scrollTop = 0;
    }
  }, [page]);

  // Helper to replace "Bureau" (and variations like "Bureau Bureau") with "Connected Services Hub"
  const formatRecipientName = (value: string | undefined | null): string => {
    if (!value || value === '—' || value === 'Unknown') {
      return value || 'Unknown';
    }
    // If the value is just "Bureau" repeated (e.g., "Bureau", "Bureau Bureau"), replace with "Connected Services Hub"
    if (/^(Bureau\s*)+$/i.test(value.trim())) {
      return 'Connected Services Hub';
    }
    return value;
  };

  const waitForModalConfirm = (): Promise<boolean> => {
      return new Promise(resolve => {
          resolverRef.current = resolve;
          setOpenModalConfirm(true);
      });
  };

  const hasAnyAcknowledgedBy = filteredData.some(item => {
    const name = getAcknowledgedByName(item) || (item.localAck ? 'Unknown' : null);
    return name !== null && name !== '—' && name.trim() !== '';
  });

  const handleAcknowledge = async (alarm: BureauAlarm) => {
    if (!alarm.bureauAlarmId && !alarm.id) {
      setErrorMessage('Alarm ID is missing');
      return;
    }

    const alarmId = alarm.bureauAlarmId || alarm.id!;

    // Read acknowledged-at variant and build detailed modal content with alarm information
    const ackAt = getVariant<string|number>(alarm, ['localAckAt','LocalAckAt','acknowledgedAt']);

    // Build detailed modal content with alarm information
    const modalContent = (
      <div>
        <div className="form-entry mb-2">
          <div className="form-entry__heading">
            <h3 className='heading__title--building'>
              {alarm.siteName || alarm.siteIdentifier || `Alarm ${alarmId}`}
            </h3>
          </div>
          <div className="form-row">
            <span className="form-row__label">Site</span>
            <div className="form-row__info">{alarm.siteName || alarm.siteIdentifier || 'Unknown'}</div>
          </div>
          <div className="form-row">
            <span className="form-row__label">Recipient</span>
            <div className="form-row__info">{formatRecipientName(alarm.bureauRecipientName || alarm.bureauName)}</div>
          </div>
          <div className="form-row">
            <span className="form-row__label">Path</span>
            <div className="form-row__info">{alarm.pathToAlarm || alarm.path || alarm.alarmIdentifier || '-'}</div>
          </div>
          <div className="form-row">
            <span className="form-row__label">State</span>
            <div className="form-row__info">{alarm.bureauAlarmState || 'Unknown'}</div>
          </div>
          <div className="form-row">
            <span className="form-row__label">Last Occurrence</span>
            <div className="form-row__info">{formatDateTime(alarm.lastOccurrenceAt)}</div>
          </div>
          <div className="form-row">
            <span className="form-row__label">Status</span>
            <div className="form-row__info">{alarm.localAck ? 'Already Acknowledged' : 'Not Acknowledged'}</div>
          </div>
          {alarm.localAck && getAcknowledgedByName(alarm) && (
            <div className="form-row">
              <span className="form-row__label">Acknowledged By</span>
              <div className="form-row__info">{getAcknowledgedByName(alarm)}</div>
            </div>
          )}
          {alarm.localAck && ackAt != null && ackAt !== '' && (
            <div className="form-row">
              <span className="form-row__label">Acknowledged At</span>
              <div className="form-row__info">{formatDateTime(String(ackAt))}</div>
            </div>
          )}
        </div>
      </div>
    );

    setModalMessageText(modalContent);
    setModalHeadingText("Acknowledge Alarm");
    setOpenModalConfirm(true);

    const confirmed = await waitForModalConfirm();

    if (!confirmed) {return;}

    setAcknowledgingId(alarmId);
    setSuccessMessage(null);
    setErrorMessage(null);
    
    try {
      // Fetch the current user's profile to get their GUID
      const profileResponse = await getProfile();

      if (!profileResponse?.success || !profileResponse.data?.id) {
        setErrorMessage('Unable to retrieve user profile');
        setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
        setAcknowledgingId(null);
        return;
      }

      const acknowledgedBy = profileResponse.data.id;
      const fullName = [profileResponse.data.firstName, profileResponse.data.lastName].filter(Boolean).join(' ').trim();
      const userName = ([
        fullName,
        profileResponse.data.email,
        userProfile?.preferred_username,
        userProfile?.email,
      ].find(v => typeof v === 'string' && v.length > 0) as string) ?? 'Unknown User';

      const payload = {
        acknowledgedBy: acknowledgedBy,
        userName: userName,
      };

      const response = await localAcknowledge(alarmId, payload);

      if (response?.success) {
        // Cache the acknowledgment info locally since backend doesn't return it
        setAcknowledgedCache(prev => ({
          ...prev,
          [alarmId]: {
            userId: acknowledgedBy,
            userName: userName,
            timestamp: new Date().toISOString()
          }
        }));
        
        // Emit event to notify Sidebar to refresh
        alarmAcknowledgedEvent.emit();
        // Refresh data first, passing the acknowledged alarm ID for optimistic update
        onAcknowledged?.(1, [alarmId]);
        // Show success message using site name, auto-dismiss after 3s
        const label = alarm.siteName || alarm.siteIdentifier || String(alarmId);
        setSuccessMessage(`${label} acknowledged successfully`);
        setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
      } else {
        setErrorMessage(response?.error?.message || 'Failed to acknowledge alarm');
        setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to acknowledge alarm');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
    } finally {
      setAcknowledgingId(null);
    }
  };

  const handleCopy = async (alarm: BureauAlarm) => {
    try {
      const acknowledgedByName = getAcknowledgedByName(alarm);
      const sections = getAlarmSections(alarm, acknowledgedByName);
      const body = alarmSectionsToText(sections);
      const html = alarmSectionsToHtml(sections);

      try {
        const nav = navigator as Navigator & { clipboard?: { write?: (items: unknown[]) => Promise<void>; writeText?: (text: string) => Promise<void> } };
        const globalObj = globalThis as unknown as { ClipboardItem?: any };
        if (nav.clipboard && typeof nav.clipboard.write === 'function' && globalObj.ClipboardItem) {
          const blobPlain = new Blob([body], { type: 'text/plain' });
          const blobHtml = new Blob([html], { type: 'text/html' });
          const item = new globalObj.ClipboardItem({ 'text/plain': blobPlain, 'text/html': blobHtml });
          await nav.clipboard.write([item]);
        } else if (nav.clipboard && typeof nav.clipboard.writeText === 'function') {
          await nav.clipboard.writeText(body);
        } else {
          const div = document.createElement('div');
          div.style.position = 'fixed';
          div.style.left = '-9999px';
          div.contentEditable = 'true';
          div.innerHTML = html;
          document.body.appendChild(div);
          const range = document.createRange();
          range.selectNodeContents(div);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
          // legacy fallback
          (document as any).execCommand?.('copy');
          sel?.removeAllRanges();
          document.body.removeChild(div);
        }
      } catch (err) {
        try {
          const nav2 = navigator as Navigator & { clipboard?: { writeText?: (text: string) => Promise<void> } };
          if (nav2.clipboard && typeof nav2.clipboard.writeText === 'function') {
            await nav2.clipboard.writeText(body);
          } else {
            const ta = document.createElement('textarea');
            ta.value = body;
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            (document as any).execCommand?.('copy');
            document.body.removeChild(ta);
          }
        } catch (_) {
          /* ignore final fallback errors */
        }
      }

      setSuccessMessage('Copied alarm details to clipboard');
      setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
    } catch (err) {
      setErrorMessage('Failed to copy alarm details');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
    }
  };

  const handleEmail = async (alarm: BureauAlarm) => {
    try {
      // Determine recipient id from alarm object (backend may be number or string)
      const rawRecipientId = getVariant<number | string>(alarm, ['bureauRecipientId']);
      const recipientId = typeof rawRecipientId === 'string' ? Number(rawRecipientId) : (rawRecipientId ?? null);
      if (!recipientId) {
        setErrorMessage('No email recipient configured for this alarm');
        setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
        return;
      }

      const res = await getRecipient(recipientId);
      if (!res || !res.success || !res.data) {
        setErrorMessage('Failed to lookup recipient');
        setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
        return;
      }

      const recipient = res.data;
      
      
      // Check if this is an email recipient
      if (!recipient.isEmailRecipient) {
      console.info('Recipient is not configured for email:', recipient.contactType);
        setErrorMessage(`This recipient (${recipient.displayName || recipientId}) is configured for ${recipient.contactType || 'bureau'} notifications, not email. Please add an email recipient to the distribution group.`);
        setTimeout(() => setErrorMessage(null), TOAST_LONG_MS);
        return;
      }
      
      // For email recipients, get the email from contactValue
      const email = (recipient.contactValue || '').trim();
      
      // Validate email format (basic check)
      if (!email || !email.includes('@')) {
        console.info('Invalid or missing email address:', email);
        setErrorMessage('Recipient does not have a valid email address');
        setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
        return;
      }
      

      // Build subject and body similar to copy formatting
      const acknowledgedByName = getAcknowledgedByName(alarm);
      const subject = `[Alarm] ${alarm.siteName || alarm.siteIdentifier || ''} ${alarm.alarmIdentifier ? `- ${alarm.alarmIdentifier}` : ''}`.trim();
      const sections = getAlarmSections(alarm, acknowledgedByName);
      const body = alarmSectionsToText(sections);

      const to = (email || '').trim();
      const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      // Create and click an anchor tag (most reliable for mailto)
      const a = document.createElement('a');
      a.href = mailto;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setSuccessMessage(`Opening email to ${to}`);
      setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
    } catch (err) {
      setErrorMessage('Failed to open email compose');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
      console.info('Error in handleEmail:', err);
    }
  };

  const handleView = (alarm: BureauAlarm) => {
    // Open an in-place modal showing alarm details instead of navigating away
    setViewAlarm(alarm);
    setOpenViewModal(true);
  };
  const viewRecipient = (() => {
    if (!viewAlarm) { return '—'; }
    const value = viewAlarm.bureauRecipientName || viewAlarm.bureauName || viewAlarm.bureauEndpoint || '—';
    // If the value is just "Bureau" repeated (e.g., "Bureau", "Bureau Bureau"), replace with "Connected Services Hub"
    if (value === '—') { return value; }
    if (/^(Bureau\s*)+$/i.test(value.trim())) {
      return 'Connected Services Hub';
    }
    return value;
  })();
  const viewContact = (() => {
    if (!viewAlarm) { return '—'; }
    const a = viewAlarm as unknown as Record<string, unknown>;
    const rawId = a['bureauRecipientId'];
    const id = typeof rawId === 'number' ? rawId : (typeof rawId === 'string' && rawId.length > 0 ? Number(rawId) : undefined);
    const value = recipientContactMap[id as number] ?? '—';
    // If the value is just "Bureau" repeated, replace with "Connected Services Hub"
    if (value === '—') { return value; }
    if (/^(Bureau\s*)+$/i.test(value.trim())) {
      return 'Connected Services Hub';
    }
    return value;
  })();
  const viewAckAt = (() => {
    if (!viewAlarm) { return undefined; }
    const a = viewAlarm as unknown as Record<string, unknown>;
    const raw = a['localAckAt'] ?? a['LocalAckAt'] ?? a['acknowledgedAt'];
    if (typeof raw === 'string' && raw.length > 0) { return raw; }
    if (typeof raw === 'number') { return String(raw); }
    return undefined;
  })();
  return (
    <div className="table-container table-container--alarm-list">
      <Modal
        isOpen={openModalConfirm}
        title={modalHeadingText || "Confirm Action"}
        content={<ModalContent
          heading={modalHeadingText}
          message={modalMessageText} />}
          onConfirm={() => {
          resolverRef.current?.(true);
          setOpenModalConfirm(false);
        }}
        onCancel={() => {
          resolverRef.current?.(false);
          setOpenModalConfirm(false);
        }}
      />
      {/* View alarm details modal */}
      <Modal
        isOpen={openViewModal}
        title={viewAlarm ? `Alarm ${viewAlarm.bureauAlarmId || viewAlarm.id || ''}` : 'Alarm Details'}
        content={(
          <div>
            {viewAlarm ? (
              <div className="modal__body">
                <div><strong>Site Name:</strong> {viewAlarm.siteName || viewAlarm.siteIdentifier || '—'}</div>
                <div><strong>Site Identifier:</strong> {viewAlarm.siteIdentifier || '—'}</div>
                <div><strong>Recipient:</strong> {viewRecipient}</div>
                <div><strong>Contact:</strong> {viewContact}</div>
                <div><strong>Path:</strong> {viewAlarm.pathToAlarm || viewAlarm.path || viewAlarm.alarmIdentifier || '—'}</div>
                <div><strong>Priority:</strong> {viewAlarm.priority !== null && viewAlarm.priority !== undefined ? viewAlarm.priority : 'Not Set'}</div>
                <div><strong>Last Occurrence:</strong> {formatDateTime(viewAlarm.lastOccurrenceAt)}</div>
                <div><strong>Acknowledged:</strong> {viewAlarm.localAck ? 'Yes' : 'No'}</div>
                {viewAlarm.localAck && getAcknowledgedByName(viewAlarm) && (
                  <div><strong>Acknowledged By:</strong> {getAcknowledgedByName(viewAlarm)}</div>
                )}
                {viewAlarm.localAck && viewAckAt && (
                  <div><strong>Acknowledged At:</strong> {formatDateTime(viewAckAt)}</div>
                )}
                <div><strong>Source Alarm State:</strong> {viewAlarm.sourceAlarmState ?? '—'}</div>
                <div><strong>Alarm Message:</strong> {viewAlarm.alarmMessage ?? '—'}</div>
              </div>
            ) : (
              <div>No data</div>
            )}
          </div>
        )}
        onConfirm={() => setOpenViewModal(false)}
        onCancel={() => setOpenViewModal(false)}
      />
      {successMessage && <StatusMessage kind="success">{successMessage}</StatusMessage>}
      {errorMessage && <StatusMessage kind="error">{errorMessage}</StatusMessage>}
      <div ref={wrapperRef} className="bureau-table-overlay-wrapper">
        {isLoading && (
          <div className="bureau-table-loading-overlay">
            <LoadingSpinner message="Loading alarms…" />
          </div>
        )}
        <div className={isLoading ? 'bureau-table-content--loading' : undefined}>
      <div className="alarm-heading">
        <div className="alarm-heading__left">
          <h3 className="table-title__alarms-list mt-0">
            {(() => {
              const total = totalCount ?? filteredData.length;
              if (total <= 0) {
                return 'Connected Services Active Alarms (0 of 0)';
              }

              if (!pageSize) {
                return `Connected Services Active Alarms (${filteredData.length} of ${total})`;
              }

              const start = (page - 1) * pageSize + 1;
              const end = Math.min((page - 1) * pageSize + filteredData.length, total);
              return `Connected Services Active Alarms (${start}-${end} of ${total})`;
            })()}
          </h3>
          {(headerSubtitle || showSummary) && (
            <div className="alarm-heading__subtitle">
              {headerSubtitle}
              {showSummary && (
                <SortSummary
                  columnLabel={SORT_COLUMN_LABELS[sortColumn]}
                  sortOrder={sortOrder}
                  onReset={handleResetSort}
                />
              )}
            </div>
          )}
        </div>
        {headerActions && (
          <div className="alarm-heading__actions">
            {headerActions}
          </div>
        )}
      </div>
     
      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th className="multiselect-input">
                <input 
                  type="checkbox" 
                  onChange={onToggleSelectAll}
                  checked={filteredData.length > 0 && filteredData.every(item => selectedIds[(item.bureauAlarmId || item.id) ?? 0])}
                />
              </th>
              <th onClick={() => handleSort('siteName')} aria-sort={sortColumn === 'siteName' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'} className="th--sortable">
                Site Name <SortIcon sortOrder={sortColumn === 'siteName' ? sortOrder : null} />
              </th>
             
              <th onClick={() => handleSort('path')} aria-sort={sortColumn === 'path' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'} className="th--sortable">
                Path <SortIcon sortOrder={sortColumn === 'path' ? sortOrder : null} />
              </th>
              <th onClick={() => handleSort('priority')} aria-sort={sortColumn === 'priority' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'} className="th--sortable">
                Priority <SortIcon sortOrder={sortColumn === 'priority' ? sortOrder : null} />
              </th>
              <th onClick={() => handleSort('last')} aria-sort={sortColumn === 'last' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'} className="th--sortable">
                Last Occurrence<SortIcon sortOrder={sortColumn === 'last' ? sortOrder : null} />
              </th>
              <th onClick={() => handleSort('sourceAlarmState')} aria-sort={sortColumn === 'sourceAlarmState' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'} className="th--sortable">
                Alarm State <SortIcon sortOrder={sortColumn === 'sourceAlarmState' ? sortOrder : null} />
              </th>
              <th>
                Alarm Message
              </th>
              {hasAnyAcknowledgedBy && (
                <th onClick={() => handleSort('acknowledgedBy')} aria-sort={sortColumn === 'acknowledgedBy' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'} className="th--sortable">
                  Acknowledged By <SortIcon sortOrder={sortColumn === 'acknowledgedBy' ? sortOrder : null} />
                </th>
              )}
              <th className='actions-column'>Actions</th>
            </tr>
          </thead>
          <tbody ref={tbodyRef}>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={10} className="table-responsive-nodata-text">No Results</td>
              </tr>
            ) : (
                (sortedData || filteredData).map((item, idx) => {
                const itemId = (item.bureauAlarmId || item.id) ?? 0;
                const siteKey = item.siteRegistryId ?? item.siteGuid ?? item.siteIdentifier ?? null;
                const siteLink = siteKey ? `/sites/${encodeURIComponent(String(siteKey))}` : `/alarm/edit/${item.bureauAlarmId ?? item.id ?? idx}`;
                // Build per-row actions and omit the Acknowledge action when already acknowledged
                const actions = [
                  // Only show acknowledge action if the alarm is not already locally acknowledged
                  ...(!item.localAck ? [{
                    label: 'Acknowledge',
                    onClick: () => handleAcknowledge(item),
                    className: 'actions__item actions__item--acknowledge',
                    disabled: acknowledgingId !== null,
                  }] : []),
                  {
                    label: 'View',
                    onClick: () => handleView(item),
                    className: 'actions__item actions__item--view',
                  },
                  {
                    label: 'Email',
                    onClick: () => handleEmail(item),
                    className: 'actions__item actions__item--email',
                  },
                  {
                    label: 'Copy',
                    onClick: () => handleCopy(item),
                    className: 'actions__item actions__item--copy',
                  }
                ];

                return (
                <tr key={itemId || idx}>
                  <td className="multiselect-input">
                    <input 
                      type="checkbox" 
                      checked={!!selectedIds[itemId]}
                      onChange={() => onToggleSelect?.(itemId)}
                    />
                  </td>
                  <td>
                    {disableSiteLink ? (
                      <span className="alarm-name-text">{item.siteName || item.siteIdentifier || 'Unknown'}</span>
                    ) : (
                      <Link to={siteLink} className="alarm-name-link">
                        {item.siteName || item.siteIdentifier || 'Unknown'}
                      </Link>
                    )}
                  </td>
                  <td className="path-name">{item.pathToAlarm || item.path || item.alarmIdentifier || '-'}</td>
                  <td><span className="badge badge--priority">{item.priority !== null && item.priority !== undefined ? item.priority : 'Not Set'}</span></td>
                  <td>{formatDateTime(item.lastOccurrenceAt)}</td>
                  <td>
                    {item.sourceAlarmState ? (
                      <span className={
                        item.sourceAlarmState.toLowerCase() === 'active' && item.localAck ? 'status status__completed' :
                        item.sourceAlarmState.toLowerCase() === 'active' ? 'status status__active' :
                        item.sourceAlarmState.toLowerCase() === 'reset' ? 'status status__reset' :
                        item.sourceAlarmState.toLowerCase() === 'acknowledged' ? 'status status__completed' :
                        item.sourceAlarmState.toLowerCase() === 'normal' ? 'status status__normal' :
                        item.sourceAlarmState.toLowerCase() === 'disabled' ? 'status status__disabled' :
                        undefined
                      }>
                        {item.sourceAlarmState.toLowerCase() === 'active' && !item.localAck
                          ? 'Active - Unacknowledged'
                          : item.sourceAlarmState.toLowerCase() === 'active' && item.localAck
                          ? 'Active - Acknowledged'
                          : item.sourceAlarmState}
                      </span>
                    ) : '—'}
                  </td>
                  <td className='alarm-message-header'>
                    <span 
                      className="alarm-message"
                      onMouseEnter={(e) => handleMouseEnterTooltip(e, item.alarmMessage || '')}
                      onMouseLeave={handleMouseLeaveTooltip}
                    >
                      {item.alarmMessage || '—'}
                    </span>
                  </td>
                  {hasAnyAcknowledgedBy && (
                    <td>
                      {(() => {
                        const name = getAcknowledgedByName(item) || (item.localAck ? 'Unknown' : null);
                        if (!name || name === '—') { return '—'; }
                        if (name === 'Unknown') { return 'Unknown'; }
                        const ackDate = getAcknowledgedAtDate(item);
                        return (
                          <div className="acknowledged-by">
                            <div
                              onMouseEnter={(e) => handleMouseEnterTooltip(e, name, ackDate)}
                              onMouseLeave={handleMouseLeaveTooltip}
                              className="sidebar--avatar"
                              style={{ background: colorFromName(name) }}
                            >
                              {getInitialsFromName(name)}
                            </div>
                            {ackDate && <span className="acknowledged-by__date"><b>On:</b> {ackDate}</span>}
                          </div>
                        );
                      })()}
                      
                    </td>
                  )}
                  <td className="actions-column">
                    <div className="actions">
                      <div className="actions__list">
                        <ActionList 
                          customActions={actions}
                          disabled={acknowledgingId !== null}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })
            )}
          </tbody>
        </table>
      </div>
      </div>
      {showTooltip && (
        <div
          className="sidebar-tree__tooltip"
          style={computeTooltipStyle(tooltipPosition)}
        >
          {tooltipText}
        </div>
      )}
      </div>
    </div>
  );
};
